import type { Editor, Text } from 'slate';

import { createAlineCanvas } from '../../canvas';
import { Queries } from '../internal';
import type { LeafFontStyle } from '../internal';

const newlines = [
  0x000a, // line feed
  0x000d, // carriage return
];
const breakingSpaces = [
  0x0009, // character tabulation
  0x0020, // space
  0x2000, // en quad
  0x2001, // em quad
  0x2002, // en space
  0x2003, // em space
  0x2004, // three-per-em space
  0x2005, // four-per-em space
  0x2006, // six-per-em space
  0x2008, // punctuation space
  0x2009, // thin space
  0x200a, // hair space
  0x205f, // medium mathematical space
  0x3000, // ideographic space
];
let lazyCanvas: ReturnType<typeof createAlineCanvas> | null = null;
const cache: Cache = {};

/**
 * Lazy initialization of canvas to avoid font registration during module import.
 */
function getOrCreateContext(): ReturnType<ReturnType<typeof createAlineCanvas>['getContext']> {
  if (!lazyCanvas) lazyCanvas = createAlineCanvas();
  return lazyCanvas.getContext('2d');
}

interface Cache {
  [fontString: string]: {
    [text: string]: number;
  };
}

/**
 * Use CSS font declaration - https://www.w3.org/TR/CSS21/fonts.html#propdef-font.
 *
 * Note: Do NOT use lineHeight in this font string (e.g., 16px/1) as that seems to break NodeJS
 * measurements and breaks server-side PDF serialization.
 *
 * We explicitly specify Arial as the first fallback font (before sans-serif) to improve consistency
 * when rendering characters that are not supported by the preferred font.
 */
export function alineFontString({ fontSizePx, fontStyle, fontType, fontWeight }: LeafFontStyle) {
  return `${fontStyle} ${fontWeight} ${fontSizePx}px ${fontType}, Arial, sans-serif`;
}

export function measureText(
  editor: Pick<Editor, 'measureText'>,
  options: {
    textNode: Text;
    /** Do not emit a warning if measureText is used for justified text containing spaces. */
    suppressJustifiedWarning?: true;
    text?: string;
  }
): number {
  if (!editor.measureText) {
    const { suppressJustifiedWarning, text, textNode } = options;
    const string = text ?? textNode.text;
    const fontStyle = Queries.getLeafFontStyle(textNode);
    const font = alineFontString(fontStyle);
    if (!cache[font]) cache[font] = {};
    if (cache[font][string]) return cache[font][string];

    if (!suppressJustifiedWarning && string.includes(' '))
      console.warn(
        'measureText was called with a string containing one or more spaces.\n\nThis may be a mistake, since measureText will report an incorrect width for justified text containing spaces. To correctly account for justified text, use Queries.getTextWidthPx instead.\n\nIf justified text is handled elsewhere, or if this instance of measureText will never be called with justified text, use the suppressJustifiedWarning option to hide this warning.\n',
        new Error()?.stack
      );

    const ctx = getOrCreateContext();
    ctx.font = font;
    const textMetrics = ctx.measureText(string);
    const width = textMetrics.width;

    return (cache[font][string] = width);
  }

  return editor.measureText(editor, options);
}

function isNewline(char: string): boolean {
  return newlines.indexOf(char.charCodeAt(0)) >= 0;
}

export function isBreakingSpace(char: string): boolean {
  return breakingSpaces.indexOf(char.charCodeAt(0)) >= 0;
}

export function tokenize(text: string): string[] {
  const tokens: string[] = [];
  let token = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (isBreakingSpace(char) || isNewline(char)) {
      if (token !== '') {
        tokens.push(token);
        token = '';
      }

      tokens.push(char);
      continue;
    }

    token += char;
  }

  if (token !== '') tokens.push(token);

  return tokens;
}
